<script setup lang="ts">
import {onMounted, ref} from 'vue';
import {createObject, deleteObject, listObjects, updateObject} from '@/base/callBackend';
import {showHideLoading} from '@/base/loadingState';
import {CabDashboardKpiTarget, CabKPI, CabOrganization, SELECTOR_IS_NOT_NULL, uuidOf} from 'common';
// noinspection ES6UnusedImports - usages in <template> are not properly detected!
import {DASHBOARD_TARGET_CHANGE_EMIT, DASHBOARD_TARGET_REMOVE_EMIT} from '@/base/cabConstants';
import DashboardKpiTargetStrip from '@/views/admin/DashboardKpiTargetStrip.vue';
import PlusButton from '@/views/icons/PlusIcon.vue';
import GeneralFilter from '@/views/general/GeneralFilter.vue';

const targets         = ref<CabDashboardKpiTarget[]>([]);
const filteredTargets = ref<CabDashboardKpiTarget[]>([]);
const filterActive    = ref<boolean>(false);
const organizations   = ref<CabOrganization[]>([]);
const kpis            = ref<CabKPI[]>([]);

onMounted(async () => {
    showHideLoading(true);
    try {
        organizations.value = await listObjects(CabOrganization);
        kpis.value          = await listObjects(CabKPI, {dashboardReeks: SELECTOR_IS_NOT_NULL});
        targets.value       = await listObjects(CabDashboardKpiTarget);
    } finally {
        showHideLoading(false);
    }
});

//=====================================================================================
async function onTargetRemove(target: CabDashboardKpiTarget) {
    await deleteObject(target);
    targets.value = targets.value.filter((t) => uuidOf(t) !== uuidOf(target));
}

async function onTargetAdd() {
    filterActive.value                     = false;
    const newTarget: CabDashboardKpiTarget = new CabDashboardKpiTarget();
    await createObject(newTarget);
    targets.value = [newTarget, ...targets.value];
}

async function onTargetChange(target: CabDashboardKpiTarget) {
    const uuid: string = uuidOf(target);
    await updateObject(target);
    targets.value = targets.value.map((t) => uuidOf(t) === uuid ? target : t);
}
</script>

<template>
    <div class="flex flex-col flex-1">
        <div class="flex flex-row flex-1 mb-1">
            <PlusButton @click="onTargetAdd()"/>
            <div class="text-2xl flex-1 ml-2" data-testid="data-section-title">
                KPI targets
            </div>
            <GeneralFilter v-model:active="filterActive"
                           v-model:cab-list="targets"
                           v-model:cab-list-filtered="filteredTargets"
                           :id="'DashboardTargetList'"
            />
        </div>
        <div class="text-sm text-gray-500 mb-2">
            Een target hoort bij een organisatie en een KPI met een dashboard reeks. De regel bepaalt de kleur in het dashboard, de omschrijving wordt getoond.
        </div>
        <DashboardKpiTargetStrip
            v-for="target in filteredTargets"
            :key="uuidOf(target)"
            :target="target"
            :allOrganizations="organizations"
            :allKpis="kpis"
            v-on:[DASHBOARD_TARGET_REMOVE_EMIT]="onTargetRemove"
            v-on:[DASHBOARD_TARGET_CHANGE_EMIT]="onTargetChange"
        />
    </div>
</template>
